/**
 * Debug system types and interfaces.
 * These types define the shape of debug logging, system info, and entity info
 * used throughout the Gotti framework's debug infrastructure.
 */

// Minimal DOM reference for environments without full lib "dom".
// eslint-disable-next-line @typescript-eslint/no-empty-interface
declare global {
  // Only declare if not already present (e.g. from lib "dom").
  interface HTMLElement {}
}

/**
 * Debug log entry representing a single log message.
 */
export type DebugLogEntry = {
  /** Unique identifier for this log entry */
  id: string;
  /** Timestamp when the log was created (ms since epoch) */
  timestamp: number;
  /** Log severity level */
  level: 'log' | 'info' | 'warn' | 'error';
  /** Name of the system or entity that generated this log */
  systemName: string;
  /** Arguments passed to the log call */
  args: any[];
  /** Source of the log (client or server) */
  source: 'client' | 'server';
  /** Optional area ID if the log is area-scoped */
  areaId?: string;
  /** Optional area type if the log is area-scoped */
  areaType?: string;
  /** Optional entity ID if the log is entity-scoped */
  entityId?: string | number;
  /** Optional entity type if the log is entity-scoped */
  entityType?: string;
  /** Optional group ID for grouped logs (replaces previous log with same groupId) */
  groupId?: string;
};

/**
 * Debug information about a system.
 */
export type SystemDebugInfo = {
  /** System name */
  name: string;
  /** Optional state object for the system */
  state?: any;
  /** Optional properties object for the system */
  properties?: Record<string, any>;
  /** Optional per-source snapshots so UI can render client/server separately */
  stateBySource?: Partial<Record<'client' | 'server', any>>;
  /** Optional per-source properties */
  propertiesBySource?: Partial<Record<'client' | 'server', Record<string, any>>>;
  /** Log entries for this system */
  logs: DebugLogEntry[];
  /** Maximum number of logs to retain */
  maxLogs: number;
  /** Indicates the source of the system */
  systemType?: 'client' | 'server';
};

/**
 * Debug information about an entity.
 */
export type EntityDebugInfo = {
  /** Entity ID */
  id: string | number;
  /** Entity type/class name */
  entityType: string;
  /** Display name for the entity */
  name: string;
  /** Optional state object */
  state?: any;
  /** Optional properties object */
  properties?: Record<string, any>;
  /** Log entries for this entity */
  logs: DebugLogEntry[];
  /** Maximum number of logs to retain */
  maxLogs: number;
};

/**
 * Options for updating system debug info.
 */
export type SystemDebugUpdateOptions = {
  /** Replace existing properties/state instead of merging */
  replace?: boolean;
  /** When true (server only), broadcast the updated info to connected clients */
  sync?: boolean;
};

/**
 * Options for updating entity debug info.
 */
export type EntityDebugUpdateOptions = {
  /** Replace existing properties/state instead of merging */
  replace?: boolean;
  /** When true (server only), broadcast the updated info to connected clients */
  sync?: boolean;
};

/**
 * Debug logger interface that systems and entities use to log debug information.
 * Similar to console but routes logs through the debug system.
 */
export interface IDebugLogger {
  /** Log a message at the default level */
  log(...args: any[]): void;
  /** Log an informational message */
  info(...args: any[]): void;
  /** Log a warning message */
  warn(...args: any[]): void;
  /** Log an error message */
  error(...args: any[]): void;
  /** 
   * Create a grouped logger. Logs with the same groupId replace previous logs
   * with that groupId, useful for frequently-updated debug values.
   */
  group(groupId: string): IDebugLogger;
}

/**
 * Callable debug logger type - can be called directly as a function (logs at 'log' level)
 * or accessed as an object with log/info/warn/error methods.
 */
export type DebugLogger = IDebugLogger & ((...args: any[]) => void);

/**
 * Debug page configuration for extensible debug UI.
 */
export interface IDebugPageConfig {
  /** Unique identifier for the page */
  id: string;
  /** Display label for the tab */
  label: string;
  /** Priority for ordering (lower = higher priority, appears first) */
  priority?: number;
}

/**
 * Interface for debug page implementations.
 */
export interface IDebugPage {
  /** Configuration for this page */
  readonly config: IDebugPageConfig;
  /** Create and return the DOM element for this page */
  createElement(): HTMLElement;
  /** Called when the page becomes active/visible */
  onActivate?(): void;
  /** Called when the page becomes inactive/hidden */
  onDeactivate?(): void;
  /** Called on each update tick (if registered) */
  onUpdate?(delta: number): void;
  /** Cleanup when page is destroyed */
  destroy?(): void;
}

/**
 * Debug system interface that both ClientDebugSystem and ServerDebugSystem implement.
 */
export interface IDebugSystem {
  /** Add a log entry for a system */
  addLogEntry(entry: DebugLogEntry): void;
  /** Add a log entry for an entity */
  addEntityLogEntry(entry: DebugLogEntry): void;
  /** Update debug info for a system */
  updateSystemDebugInfo(
    systemName: string, 
    info: Partial<Omit<SystemDebugInfo, 'name' | 'logs'>>,
    options?: SystemDebugUpdateOptions
  ): void;
  /** Update debug info for an entity */
  updateEntityDebugInfo(
    entityId: string | number,
    info: Partial<Omit<EntityDebugInfo, 'id' | 'logs'>>,
    options?: EntityDebugUpdateOptions
  ): void;
  /** Get debug info for a system */
  getSystemDebugInfo(systemName: string): SystemDebugInfo | undefined;
  /** Get debug info for an entity */
  getEntityDebugInfo(entityId: string | number): EntityDebugInfo | undefined;
  /** Get all system debug info */
  getAllSystemDebugInfo(): Record<string, SystemDebugInfo>;
  /** Get all entity debug info */
  getAllEntityDebugInfo(): Record<string, EntityDebugInfo>;
  /** Create a debug logger for a system */
  createSystemLogger(systemName: string): DebugLogger;
  /** Create a debug logger for an entity */
  createEntityLogger(entityId: string | number, entityType: string): DebugLogger;
  /** Check if debug system is enabled */
  readonly enabled: boolean;
}

/**
 * Debug system constants - prefixed to avoid collision with user-defined messages.
 * Uses a distinctive prefix that users would never accidentally use.
 */
export const DEBUG_SYSTEM_NAME = '____gotti_debug_system____';
export const DEBUG_MESSAGE_PREFIX = '____gotti_debug____';

/**
 * Debug message types for internal communication.
 */
export const DEBUG_MESSAGES = {
  /** Log entry sync from server to client */
  LOG_ENTRY: `${DEBUG_MESSAGE_PREFIX}log_entry`,
  /** System debug info sync */
  SYSTEM_INFO_SYNC: `${DEBUG_MESSAGE_PREFIX}system_info_sync`,
  /** Entity debug info sync */
  ENTITY_INFO_SYNC: `${DEBUG_MESSAGE_PREFIX}entity_info_sync`,
  /** Request debug state from server */
  REQUEST_DEBUG_STATE: `${DEBUG_MESSAGE_PREFIX}request_state`,
  /** Full debug state response */
  FULL_DEBUG_STATE: `${DEBUG_MESSAGE_PREFIX}full_state`,
  /** Area info update */
  AREA_INFO: `${DEBUG_MESSAGE_PREFIX}area_info`,
  /** Enable/disable debug on server */
  SET_ENABLED: `${DEBUG_MESSAGE_PREFIX}set_enabled`,
} as const;

/**
 * Noop debug logger for when debug is disabled.
 * All methods do nothing to avoid overhead.
 */
export const NOOP_DEBUG_LOGGER: DebugLogger = Object.assign(
  function noopLog(..._args: any[]) {},
  {
    log: function noopLog(..._args: any[]) {},
    info: function noopInfo(..._args: any[]) {},
    warn: function noopWarn(..._args: any[]) {},
    error: function noopError(..._args: any[]) {},
    group: function noopGroup(_groupId: string): IDebugLogger {
      return NOOP_DEBUG_LOGGER;
    },
  }
);
